import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@fragments';
import { IconBan, IconPhoto, IconSquareFilled } from '@tabler/icons-react';
import SolidPicker from './SolidPicker';
import GradientEditor from './GradientEditor';
import ImageFillEditor from './ImageFillEditor';
import { fillColor, luminance, mix } from '../../../model/colors';

// A rounded square fading out: the gradient tab's mark.
const GradientGlyph = props => <svg viewBox="0 0 24 24" fill="none" {...props}>
  <defs><linearGradient id="art-fill-gradient-glyph" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="currentColor" /><stop offset="1" stopColor="currentColor" stopOpacity="0.12" /></linearGradient></defs>
  <rect x="3.5" y="3.5" width="17" height="17" rx="4" fill="url(#art-fill-gradient-glyph)" stroke="currentColor" strokeWidth="1.5" />
</svg>;
const MODES = [
  { id: 'solid', icon: IconSquareFilled, label: 'fillSolid' },
  { id: 'gradient', icon: GradientGlyph, label: 'fillGradient' },
  { id: 'image', icon: IconPhoto, label: 'fillImageMode' },
];
const kindOf = paint => (!paint || paint.type === 'solid' ? 'solid' : paint.type === 'image' ? 'image' : 'gradient');
export const toPaint = value => (value && typeof value === 'object' ? value : value ? { type: 'solid', color: value } : null);

// The fill picker (Figma's Custom tab): a color, a gradient or a photo, as the target takes
// them (`modes`). Switching tabs converts what is there (a color becomes a gradient from
// it, a gradient's first stop becomes the color) and brings back what the tab last held,
// so trying a gradient and returning loses nothing. The photo tab opens empty until a
// photo is chosen; until then the target keeps its paint.
export default function FillPicker({ value, onChange, modes = ['solid'], allowNone = false, alpha = false, brand = [], used = [], image, close }) {
  const { t } = useTranslation('artEditor');
  const paint = toPaint(value);
  const kind = kindOf(paint);
  const [mode, setMode] = useState(kind);
  useEffect(() => { setMode(kind); }, [kind]);
  const remembered = useRef({});
  if (paint) remembered.current[kind] = paint;
  const switchTo = next => {
    setMode(next);
    if (next === kind) return;
    const base = fillColor(paint) || remembered.current.solid?.color || '#1f4d3a';
    if (next === 'solid') onChange(remembered.current.solid && kind === 'image' ? remembered.current.solid : { type: 'solid', color: base });
    // A new gradient runs from the color toward light (a dark color) or dark (a light one), so it shows.
    const end = luminance(base) < 0.25 ? mix(base, '#ffffff', 0.65) : mix(base, '#000000', 0.55);
    if (next === 'gradient') onChange(remembered.current.gradient || { type: 'linear', angle: 180, stops: [{ offset: 0, color: base, opacity: 1 }, { offset: 1, color: end, opacity: 1 }] });
    if (next === 'image' && remembered.current.image) onChange(remembered.current.image);
  };
  const tabs = MODES.filter(item => modes.includes(item.id));
  return <div className="art-fill">
    {(tabs.length > 1 || allowNone) && <div className="art-fill__tabs" role="tablist" aria-label={t('fill')}>
      {tabs.map(item => <Tooltip key={item.id} content={t(item.label)}>
        <button type="button" role="tab" className={`art-fill__tab ${mode === item.id && paint ? 'is-active' : ''} ${mode === item.id ? 'is-current' : ''}`} aria-selected={mode === item.id}
          aria-label={t(item.label)} onClick={() => switchTo(item.id)}><item.icon aria-hidden="true" /></button>
      </Tooltip>)}
      {allowNone && <>
        <span className="art-fill__spacer" />
        <Tooltip content={t('colorNone')}>
          <button type="button" className={`art-fill__tab ${!paint ? 'is-active' : ''}`} aria-pressed={!paint} aria-label={t('colorNone')} onClick={() => onChange(null)}><IconBan aria-hidden="true" /></button>
        </Tooltip>
      </>}
    </div>}
    {mode === 'solid' && <SolidPicker color={paint?.type === 'solid' ? paint.color : fillColor(paint) || '#ffffff'} alpha={paint?.type === 'solid' ? paint.opacity ?? 1 : 1} showAlpha={alpha}
      brand={brand} used={used} onChange={({ color, alpha: opacity }) => onChange(alpha && opacity < 1 ? { type: 'solid', color, opacity } : { type: 'solid', color })} />}
    {mode === 'gradient' && kind === 'gradient' && <GradientEditor fill={paint} onChange={onChange} brand={brand} used={used} />}
    {mode === 'image' && <ImageFillEditor fill={kind === 'image' ? paint : null} onChange={onChange} tools={image} close={close} />}
  </div>;
}
